'use client';

import React, { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { usePerformanceLabsIndia } from '../buy/india/PerformanceLabsIndiaContext';
import PlanCard from '../buy/india/PlanCard';
import type { PerformanceLabPlan } from '../buy/india/types';
import {
  IvContentShell,
  IvSectionHeading,
  IvSectionLabel,
  IvSectionRoot,
} from './ivLoungeSectionLayout';
import {
  IV_LOUNGE_LINEUP_ANCHOR_ID,
  IV_LOUNGE_PLAN_ICONS,
  IV_LOUNGE_SECTION_ID,
} from './ivLoungePageAssets';
import styles from './IvLoungeLineupSection.module.css';

export const IvLoungeLineupSection: React.FC = () => {
  const t = useTranslations('performance-lab');
  const tPlans = useTranslations('diagnostics');
  const { sections } = usePerformanceLabsIndia();

  const section = useMemo(
    () => sections.find((s) => s.id === IV_LOUNGE_SECTION_ID),
    [sections],
  );

  const subcategories = section?.subcategories ?? [];
  const [activeSubcategory, setActiveSubcategory] = useState<string | null>(
    null,
  );

  const activeSubId = activeSubcategory ?? subcategories[0]?.id ?? null;

  const displayedPlans: PerformanceLabPlan[] = useMemo(() => {
    if (!section) return [];
    if (subcategories.length > 0) {
      const found = subcategories.find((sc) => sc.id === activeSubId);
      return found ? found.plans : subcategories[0]?.plans ?? [];
    }
    return section.plans;
  }, [section, subcategories, activeSubId]);

  if (!section || displayedPlans.length === 0) return null;

  return (
    <IvSectionRoot
      id={IV_LOUNGE_LINEUP_ANCHOR_ID}
      aria-label={t('ivLounge.lineup.aria')}
    >
      <IvContentShell className={styles.shell}>
        <div className={styles.header}>
          <IvSectionLabel>
            {t('ivLounge.lineup.overline')}
          </IvSectionLabel>
          <IvSectionHeading>
            {t('ivLounge.lineup.heading')}
          </IvSectionHeading>
        </div>

        {subcategories.length > 1 && (
          <div
            className={styles.tabBar}
            role="tablist"
            aria-label={t('ivLounge.lineup.tabsAria')}
          >
            {subcategories.map((sc) => {
              const isActive = sc.id === activeSubId;
              return (
                <button
                  key={sc.id}
                  className={styles.tabItem}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveSubcategory(sc.id)}
                >
                  {tPlans(sc.label)}
                </button>
              );
            })}
          </div>
        )}

        <div className={styles.grid}>
          {displayedPlans.map((plan) => {
            const icon = IV_LOUNGE_PLAN_ICONS[plan.id];
            const cardPlan = icon ? { ...plan, thumbnailUrl: icon } : plan;
            return (
              <div
                className={styles.planCardAnchor}
                key={plan.id}
                data-plan-id={plan.id}
              >
                <PlanCard
                  plan={cardPlan}
                  variant="compact"
                  sectionId={section.id}
                />
              </div>
            );
          })}
        </div>
      </IvContentShell>
    </IvSectionRoot>
  );
};
